import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Text,
  useColorModeValue,
  Grid,
  GridItem,
  Spinner,
} from '@chakra-ui/react';

interface Team {
  id: string;
  name: string;
}
interface Division {
  id: string;
  name: string;
  teams: Team[];
}
interface Conference {
  id: string;
  name: string;
  color_primary?: string;
  color_text?: string;
  divisions: Division[];
}
interface League {
  conferences: Conference[];
}

interface SelectTeamProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (teamId: string) => void;
  league: League;
  myTeam?: number;
  excludeMyTeam?: boolean;
}

// Helper to darken a hex color
function darkenHex(hex: string, amount = 0.9) {
  // Remove # if present
  hex = hex.replace('#', '');
  if (hex.length !== 6) return '#222';
  const num = parseInt(hex, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.floor(r * (1 - amount));
  g = Math.floor(g * (1 - amount));
  b = Math.floor(b * (1 - amount));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const SelectTeam: React.FC<SelectTeamProps> = ({ isOpen, onClose, onSelect, league, myTeam = null, excludeMyTeam = false }) => {
  const bg = useColorModeValue('gray.900', 'gray.900');
  const cardBg = useColorModeValue('gray.800', 'gray.800');
  const border = useColorModeValue('gray.700', 'gray.700');
  const textColor = useColorModeValue('white', 'white');
  const [isLoading, setIsLoading] = useState(false);

  // Early return if no league data
  if (!league || !league.conferences) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={bg} color={textColor} borderRadius="lg" boxShadow="2xl">
          <ModalHeader>Choose a Team</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text>No league data available. Please try again later.</Text>
            <Flex justify="flex-end" mt={8}>
              <Button onClick={onClose} colorScheme="blue" variant="outline">Close</Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // Find my team object if provided
  let myTeamObj: Team | undefined;
  if (myTeam) {

    league.conferences.forEach(conf => {
      conf.divisions.forEach(div => {
        div.teams.forEach(team => {
       
          if (Number(team.id) === myTeam) {

            myTeamObj = team;
          }
        });
      });
    });

  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={bg} color={textColor} borderRadius="lg" boxShadow="2xl">
        <ModalHeader>Choose a Team</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isLoading && (
            <Flex justify="center" mb={4} p={4}>
              <Spinner size="lg" color="blue.500" />
              <Text ml={3} color="blue.300" fontWeight="bold">Loading team data...</Text>
            </Flex>
          )}
          {myTeam && myTeamObj && !excludeMyTeam && (
            <Flex mb={4} align="center">
              <Text mr={2} color="blue.300" fontWeight="bold">Switch to My Team:</Text>
              <Button 
                colorScheme="blue" 
                size="sm" 
                fontWeight="bold"                 
                onClick={() => {
                  if (myTeamObj) {
                    setIsLoading(true);
                    onSelect(myTeamObj.id);
                  }
                }}
                isDisabled={isLoading}
              >
                {myTeamObj.name}
              </Button>
            </Flex>
          )}
          <Grid templateColumns={{ base: '1fr', md: `repeat(${league.conferences.length}, 1fr)` }}  gap={8}>
            {league.conferences.map(conf => (

              <GridItem key={conf.id}>
                <Box mb={8} borderRadius="md" px={3} py={2}
                  style={(() => {
                    let color = conf.color_primary ? conf.color_primary.trim() : '';
                    if (color && !color.startsWith('#') && /^([0-9A-Fa-f]{6})$/.test(color)) color = '#' + color;
                    
                    const lowerColor = color.toLowerCase();
                    if (lowerColor === 'red') {
                      color = '#FF0000';
                    }
                    if (lowerColor === 'blue') {
                      color = '#0000FF';
                    }
                    
                    const isHex = /^#[0-9A-Fa-f]{6}$/.test(color);
                    return {
                      background: isHex
                        ? `linear-gradient(135deg, ${color} 0%, ${darkenHex(color)} 100%)`
                        : color || '#444',
                      color: conf.color_text || 'white',
                      fontWeight: 'bold',
                      fontSize: 'lg',
                      borderRadius: '0.375rem',
                      marginBottom: '0.5rem',
                      padding: '0.5rem 0.75rem',
                    };
                  })()}
                >
                  {conf.name}
                </Box>
                <Box mb={6} mt={6}></Box>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={3}>
                  {conf.divisions.map(div => (
                    <Box key={div.id} minW="0">
                      <Text fontWeight="semibold" color="gray.300" mb={2}>{div.name}</Text>
                      <Box bg={cardBg} borderRadius="md" borderWidth="1px" borderColor={border} px={0} py={1}>
                        {div.teams.map(team => (
                          <Button
                            key={team.id}
                            variant="ghost"
                            colorScheme={Number(team.id) === myTeam ? 'blue' : undefined}
                            color={textColor}
                            width="100%"
                            justifyContent="flex-start"
                            mb={0}
                            fontWeight={Number(team.id) === myTeam ? 'bold' : 'normal'}
                            fontSize="sm"
                            minH="32px"
                            onClick={() => {
                              setIsLoading(true);
                              onSelect(team.id);
                            }}
                            isDisabled={isLoading || (excludeMyTeam && Number(team.id) === myTeam)}
                            title={team.name}
                            _hover={{ bg: 'whiteAlpha.100' }}
                          >
                            <Text
                              whiteSpace="nowrap"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              width="100%"
                              textAlign="left"
                            >
                              {team.name}
                            </Text>
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Grid>
              </GridItem>
            ))}
          </Grid>
          <Flex justify="flex-end" mt={8}>
            <Button onClick={onClose} colorScheme="blue" variant="outline">Close</Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SelectTeam; 